import type Experiments from '@wix/wix-experiments';
import { type PaymentBreakdown } from '../../types/PaymentBreakdown';
import { type ShippingMethod } from '../../types/Shipping';
import { type OrderItem } from '../../types/OrderItem';
import { transformPaymentBreakdownToApplePay } from './applePayDataMapper';

/**
 * Creates ApplePayPaymentRequest
 */

export interface ApplePayPaymentRequestParams {
  paymentAmount: string;
  paymentLabel: string;
  paymentBreakdown: PaymentBreakdown;
  shippingMethods: ShippingMethod[];
  currencyCode: string;
  countryCode: string;
  merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[];
  supportedNetworks: string[];
  orderItems?: OrderItem[];
  requestShipping: boolean;
  experiments: Experiments;
}

export const createApplePayPaymentRequest = ({
  countryCode,
  paymentAmount,
  paymentLabel,
  paymentBreakdown,
  shippingMethods,
  currencyCode,
  supportedNetworks,
  merchantCapabilities,
  orderItems,
  requestShipping,
  experiments,
}: ApplePayPaymentRequestParams): ApplePayJS.ApplePayPaymentRequest => {
  /**
   * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/2216120-requiredbillingcontactfields
   */
  const requiredBillingContactFields = ['postalAddress'] as ApplePayJS.ApplePayContactField[];
  /**
   * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest/2216121-requiredshippingcontactfields
   */
  const requiredShippingContactFields = ['name', 'phone', 'email'] as ApplePayJS.ApplePayContactField[];

  if (requestShipping) {
    requiredShippingContactFields.push('postalAddress');
  }

  return {
    countryCode,
    total: {
      label: paymentLabel,
      amount: paymentAmount,
    },
    lineItems: transformPaymentBreakdownToApplePay(paymentBreakdown, orderItems),
    shippingMethods,
    currencyCode,
    supportedNetworks,
    merchantCapabilities,
    requiredBillingContactFields,
    requiredShippingContactFields,
  };
};
